/**
 * Date/time formatting helpers.
 *
 * The backend serializes timestamps as UTC ISO-8601 strings (e.g.
 * "2026-05-22T10:44:00+00:00"). `new Date()` parses those into a correct
 * instant, and `toLocale*` then renders them in the *viewer's* browser
 * timezone - so a user in India and a user in London each see their own
 * local time for the same underlying moment.
 */

/** Format a UTC ISO-8601 timestamp as a local date + time string (no seconds). */
export function formatDateTime(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  // Date order stays locale-adaptive (each viewer keeps their own
  // day/month/year arrangement), but the time is forced to 12-hour AM/PM
  // (`hour12: true`) for consistency across the app - a bare 24-hour value
  // like "13:13" was read as ambiguous; "1:13 PM" is unmistakable. Seconds
  // are dropped as noise for these timestamps.
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/** Format a UTC ISO-8601 timestamp as a local date string. */
export function formatDate(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

/**
 * Convert a bare "HH:MM" UTC time-of-day to the viewer's local "HH:MM".
 *
 * Used for the admin-configured daily sync time, which is stored and sent
 * as a UTC time-of-day. The wall-clock value differs per viewer (e.g.
 * "10:30" UTC shows as "4:00 PM" for a user in India).
 *
 * Forces 12-hour AM/PM (`hour12: true`) rather than letting the locale decide.
 * This label appears on the "Schedule for HH:MM" sync button/dialog, where a
 * bare 24-hour value like "14:15" was read as ambiguous; "2:15 PM" is
 * unmistakable. Other timestamps (formatDateTime) stay locale-adaptive.
 */
export function formatTimeUTC(hhmm?: string | null): string {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const now = new Date();
  const d = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), h, m, 0,
  ));
  return d.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}
